import * as React from 'react';
import {
    List,
    useDataProvider,
    useListContext,
    useNotify,
    useRefresh,
} from 'react-admin';
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    MenuItem,
    LinearProgress,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { getAccessToken } from '../authProvider';
import { API_BASE_URL } from '../config';
import {
    AssessmentRecord,
    AssessmentDimensionDetail,
    AssessmentDimensionOption,
    AssessmentLevelDetail,
    AssessmentLevelOption,
    getAssessmentDimensionId,
    getAssessmentDimensionLabel,
    getAssessmentLevelId,
    getAssessmentLevelLabel,
} from './types';

type AssessmentFormState = {
    dimensionId: number;
    levelId: number;
    title: string;
    description: string;
    content: string;
    uid: string;
    status: number;
};

const defaultFormState: AssessmentFormState = {
    dimensionId: 0,
    levelId: 0,
    title: '',
    description: '',
    content: '',
    uid: '',
    status: 9,
};

const defaultLevelDetail: AssessmentLevelDetail = {
    level: 1,
    ageMin: 0,
    ageMax: 0,
};

const defaultDimensionDetail: AssessmentDimensionDetail = {
    dimension: '',
    idx: 1,
};

const statusChoices = [
    { id: 0, name: 'Disabled' },
    { id: 1, name: 'Draft' },
    { id: 9, name: 'Enabled' },
];

const LEVEL_COLUMN_WIDTH = 60;
const MATRIX_CELL_SIZE = 240;
const getStreamPlayerUrl = (uid?: string) =>
    uid ? `https://iframe.videodelivery.net/${uid}` : '';
const normalizeStreamState = (value?: string) => (value || '').toLowerCase();
const isReadyStreamState = (state?: string) => normalizeStreamState(state) === 'ready';

const CellContent = ({
    records,
    onEditRecord,
}: {
    records: AssessmentRecord[];
    onEditRecord: (record: AssessmentRecord) => void;
}) => {
    if (!records.length) {
        return (
            <Box sx={{ width: '100%', textAlign: 'center' }}>
                <Typography variant="body2" color="text.secondary">
                    -
                </Typography>
            </Box>
        );
    }

    return (
        <Stack spacing={1} onClick={event => event.stopPropagation()}>
            {records.map(record => {
                const playerUrl = getStreamPlayerUrl(record.uid);

                return (
                    <Stack
                        key={record.id}
                        spacing={0.75}
                        alignItems="flex-start"
                        sx={{ width: '100%' }}
                    >
                        <Tooltip
                            arrow
                            title={(
                                <Box>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                                        {record.title}
                                    </Typography>
                                    <Typography variant="body2">
                                        {record.description || '-'}
                                    </Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                        {record.content || '-'}
                                    </Typography>
                                </Box>
                            )}
                        >
                            {playerUrl ? (
                                <Box
                                    onClick={event => {
                                        event.stopPropagation();
                                        onEditRecord(record);
                                    }}
                                    sx={{
                                        width: '100%',
                                        aspectRatio: '16 / 9',
                                        borderRadius: 1,
                                        overflow: 'hidden',
                                        backgroundColor: '#000',
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <Box
                                        component="iframe"
                                        src={playerUrl}
                                        loading="lazy"
                                        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                                        allowFullScreen
                                        sx={{
                                            display: 'block',
                                            width: '100%',
                                            height: '100%',
                                            border: 0,
                                            backgroundColor: '#000',
                                        }}
                                    />
                                </Box>
                            ) : (
                                <Box
                                    onClick={event => {
                                        event.stopPropagation();
                                        onEditRecord(record);
                                    }}
                                    sx={{
                                        width: '100%',
                                        aspectRatio: '16 / 9',
                                        borderRadius: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        border: '1px dashed',
                                        borderColor: 'divider',
                                        color: 'text.secondary',
                                        fontSize: 12,
                                        cursor: 'pointer',
                                    }}
                                >
                                    No Stream
                                </Box>
                            )}
                        </Tooltip>
                    </Stack>
                );
            })}
        </Stack>
    );
};

const AssessmentMatrixTable = () => {
    const { data, isPending, meta } = useListContext<AssessmentRecord>();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();
    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [dialogMode, setDialogMode] = React.useState<'create' | 'edit'>('create');
    const [editingId, setEditingId] = React.useState<number | null>(null);
    const [saving, setSaving] = React.useState(false);
    const [deletingAssessment, setDeletingAssessment] = React.useState(false);
    const [uploadingVideo, setUploadingVideo] = React.useState(false);
    const [uploadProgress, setUploadProgress] = React.useState(0);
    const [uploadError, setUploadError] = React.useState('');
    const [uploadStatus, setUploadStatus] = React.useState('');
    const [streamState, setStreamState] = React.useState('');
    const [previewPlaybackUrl, setPreviewPlaybackUrl] = React.useState('');
    const [checkingStreamReady, setCheckingStreamReady] = React.useState(false);
    const [levelDialogOpen, setLevelDialogOpen] = React.useState(false);
    const [levelLoading, setLevelLoading] = React.useState(false);
    const [levelSaving, setLevelSaving] = React.useState(false);
    const [levelError, setLevelError] = React.useState('');
    const [levelRows, setLevelRows] = React.useState<AssessmentLevelDetail[]>([]);
    const [deletedLevelIds, setDeletedLevelIds] = React.useState<number[]>([]);
    const [dimensionDialogOpen, setDimensionDialogOpen] = React.useState(false);
    const [dimensionLoading, setDimensionLoading] = React.useState(false);
    const [dimensionSaving, setDimensionSaving] = React.useState(false);
    const [dimensionError, setDimensionError] = React.useState('');
    const [dimensionRows, setDimensionRows] = React.useState<AssessmentDimensionDetail[]>([]);
    const [deletedDimensionIds, setDeletedDimensionIds] = React.useState<number[]>([]);
    const videoInputRef = React.useRef<HTMLInputElement | null>(null);
    const [formState, setFormState] = React.useState<AssessmentFormState>(defaultFormState);

    const records = data || [];

    const dimensionOptions = React.useMemo(() => {
        const dimensionFromMeta = (meta as any)?.dimension;
        if (Array.isArray(dimensionFromMeta) && dimensionFromMeta.length > 0) {
            return dimensionFromMeta
                .map((item: AssessmentDimensionOption) => ({
                    id: Number(item.id),
                    label: item.dimension,
                }))
                .filter(item => Number.isFinite(item.id));
        }

        const values = new Map<number, string>();

        records.forEach(record => {
            const dimensionId = getAssessmentDimensionId(record);
            if (dimensionId === undefined) return;

            values.set(
                dimensionId,
                getAssessmentDimensionLabel(record) || String(dimensionId)
            );
        });

        return Array.from(values.entries())
            .map(([id, label]) => ({ id, label }))
            .sort((a, b) => a.id - b.id);
    }, [meta, records]);

    const levelOptions = React.useMemo(() => {
        const levelFromMeta = (meta as any)?.level;
        if (Array.isArray(levelFromMeta) && levelFromMeta.length > 0) {
            return levelFromMeta
                .map((item: AssessmentLevelOption) => ({
                    id: Number(item.id),
                    label: String(item.level),
                }))
                .filter(item => Number.isFinite(item.id));
        }

        const values = new Map<number, string>();

        records.forEach(record => {
            const levelId = getAssessmentLevelId(record);
            if (levelId === undefined) return;

            values.set(levelId, getAssessmentLevelLabel(record) || String(levelId));
        });

        return Array.from(values.entries())
            .map(([id, label]) => ({ id, label }))
            .sort((a, b) => a.id - b.id);
    }, [meta, records]);

    const tableMinWidth =
        LEVEL_COLUMN_WIDTH + dimensionOptions.length * MATRIX_CELL_SIZE;

    const grouped = React.useMemo(() => {
        const map = new Map<number, Map<number, AssessmentRecord[]>>();

        records.forEach(record => {
            const levelId = getAssessmentLevelId(record);
            const dimensionId = getAssessmentDimensionId(record);

            if (levelId === undefined || dimensionId === undefined) return;

            if (!map.has(levelId)) {
                map.set(levelId, new Map());
            }
            const levelMap = map.get(levelId)!;
            if (!levelMap.has(dimensionId)) {
                levelMap.set(dimensionId, []);
            }
            levelMap.get(dimensionId)!.push(record);
        });

        return map;
    }, [records]);

    const usedLevelIds = React.useMemo(() => {
        const ids = new Set<number>();
        records.forEach(record => {
            const levelId = getAssessmentLevelId(record);
            if (levelId !== undefined) {
                ids.add(levelId);
            }
        });
        return ids;
    }, [records]);

    const usedDimensionIds = React.useMemo(() => {
        const ids = new Set<number>();
        records.forEach(record => {
            const dimensionId = getAssessmentDimensionId(record);
            if (dimensionId !== undefined) {
                ids.add(dimensionId);
            }
        });
        return ids;
    }, [records]);

    const openCreateDialog = React.useCallback((levelId: number, dimensionId: number) => {
        setDialogMode('create');
        setEditingId(null);
        setUploadError('');
        setUploadStatus('');
        setUploadProgress(0);
        setStreamState('');
        setPreviewPlaybackUrl('');
        setCheckingStreamReady(false);
        setFormState({
            ...defaultFormState,
            levelId,
            dimensionId,
        });
        setDialogOpen(true);
    }, []);

    const normalizeLevelRows = React.useCallback((rows: AssessmentLevelDetail[]) => {
        return rows
            .map(item => ({
                id: item.id,
                level: Number(item.level),
                ageMin: Number(item.ageMin),
                ageMax: Number(item.ageMax),
            }))
            .filter(item => [item.level, item.ageMin, item.ageMax].every(Number.isFinite))
            .sort((a, b) => a.level - b.level);
    }, []);

    const loadAssessmentLevels = React.useCallback(async () => {
        setLevelLoading(true);
        setLevelError('');
        try {
            const { data: levels } = await dataProvider.getList('admin/assessment-levels', {
                pagination: { page: 1, perPage: 200 },
                sort: { field: 'level', order: 'ASC' },
                filter: {},
            });

            const normalized = normalizeLevelRows((levels || []) as AssessmentLevelDetail[]);
            setLevelRows(normalized.length ? normalized : [{ ...defaultLevelDetail }]);
            setDeletedLevelIds([]);
        } catch (error: any) {
            const message = error?.message || 'Failed to load levels';
            setLevelError(message);
            notify(message, { type: 'error' });
        } finally {
            setLevelLoading(false);
        }
    }, [dataProvider, normalizeLevelRows, notify]);

    const openLevelDialog = React.useCallback(() => {
        setLevelDialogOpen(true);
        void loadAssessmentLevels();
    }, [loadAssessmentLevels]);

    const normalizeDimensionRows = React.useCallback((rows: AssessmentDimensionDetail[]) => {
        return rows
            .map(item => ({
                id: item.id,
                dimension: String(item.dimension || '').trim(),
                idx: Number(item.idx),
            }))
            .filter(item => item.dimension.length > 0 && Number.isFinite(item.idx))
            .sort((a, b) => a.idx - b.idx);
    }, []);

    const loadAssessmentDimensions = React.useCallback(async () => {
        setDimensionLoading(true);
        setDimensionError('');
        try {
            const { data: dimensions } = await dataProvider.getList('admin/assessment-dimensions', {
                pagination: { page: 1, perPage: 200 },
                sort: { field: 'id', order: 'ASC' },
                filter: {},
            });

            const normalized = normalizeDimensionRows((dimensions || []) as AssessmentDimensionDetail[]);
            setDimensionRows(normalized.length ? normalized : [{ ...defaultDimensionDetail }]);
            setDeletedDimensionIds([]);
        } catch (error: any) {
            const message = error?.message || 'Failed to load dimensions';
            setDimensionError(message);
            notify(message, { type: 'error' });
        } finally {
            setDimensionLoading(false);
        }
    }, [dataProvider, normalizeDimensionRows, notify]);

    const openDimensionDialog = React.useCallback(() => {
        setDimensionDialogOpen(true);
        void loadAssessmentDimensions();
    }, [loadAssessmentDimensions]);

    const closeDimensionDialog = React.useCallback(() => {
        if (dimensionSaving) return;
        setDimensionDialogOpen(false);
    }, [dimensionSaving]);

    const setDimensionRowField =
        (index: number, key: keyof AssessmentDimensionDetail) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const nextValue =
                key === 'idx' ? Number(event.target.value) : event.target.value;
            setDimensionRows(prev =>
                prev.map((row, rowIndex) =>
                    rowIndex === index
                        ? {
                              ...row,
                              [key]: nextValue,
                          }
                        : row
                )
            );
        };

    const addDimensionRow = React.useCallback(() => {
        setDimensionRows(prev => {
            const maxIdx = prev.reduce(
                (max, item) => Math.max(max, Number(item.idx) || 0),
                0
            );

            return [
                ...prev,
                {
                    ...defaultDimensionDetail,
                    idx: maxIdx + 1,
                },
            ];
        });
    }, []);

    const removeDimensionRow = React.useCallback((index: number) => {
        setDimensionRows(prev => {
            const target = prev[index];
            if (target?.id && usedDimensionIds.has(target.id)) {
                notify(`Dimension ${target.dimension} is in use and cannot be deleted`, {
                    type: 'warning',
                });
                return prev;
            }

            if (target?.id) {
                setDeletedDimensionIds(ids =>
                    ids.includes(target.id as number)
                        ? ids
                        : [...ids, target.id as number]
                );
            }

            return prev.filter((_, rowIndex) => rowIndex !== index);
        });
    }, [notify, usedDimensionIds]);

    const validateDimensionRows = React.useCallback((rows: AssessmentDimensionDetail[]) => {
        if (!rows.length) {
            return 'Dimension data is required';
        }

        if (rows.some(item => !Number.isFinite(item.idx) || item.idx < 1)) {
            return 'Dimension idx must be a positive number';
        }

        const names = rows.map(item => item.dimension.toLowerCase());
        const unique = new Set(names);
        if (unique.size !== names.length) {
            return 'Dimension name cannot be duplicated';
        }

        const idxValues = rows.map(item => item.idx);
        const uniqueIdx = new Set(idxValues);
        if (uniqueIdx.size !== idxValues.length) {
            return 'Dimension idx cannot be duplicated';
        }

        return '';
    }, []);

    const saveAssessmentDimensions = React.useCallback(async () => {
        const normalized = normalizeDimensionRows(dimensionRows);
        if (normalized.length > 0) {
            const validationError = validateDimensionRows(normalized);
            if (validationError) {
                setDimensionError(validationError);
                notify(validationError, { type: 'warning' });
                return;
            }
        }

        const deletePayload = deletedDimensionIds.map(id => ({
            id,
            deleted: true,
        }));

        if (!normalized.length && !deletePayload.length) {
            const message = 'No dimension changes to save';
            setDimensionError(message);
            notify(message, { type: 'warning' });
            return;
        }

        setDimensionSaving(true);
        setDimensionError('');
        try {
            const updatePayload = normalized.map(item => {
                if (item.id) {
                    return {
                        id: item.id,
                        dimension: item.dimension,
                        idx: item.idx,
                    };
                }

                return {
                    dimension: item.dimension,
                    idx: item.idx,
                };
            });

            const payload = [...updatePayload, ...deletePayload];

            await dataProvider.update('admin/assessment-dimensions', {
                id: 'batch',
                data: payload,
                previousData: {},
            });

            notify('Assessment dimensions updated', { type: 'success' });
            setDimensionDialogOpen(false);
            setDeletedDimensionIds([]);
            refresh();
        } catch (error: any) {
            const message = error?.message || 'Failed to update dimensions';
            setDimensionError(message);
            notify(message, { type: 'error' });
        } finally {
            setDimensionSaving(false);
        }
    }, [
        dataProvider,
        deletedDimensionIds,
        dimensionRows,
        normalizeDimensionRows,
        notify,
        refresh,
        validateDimensionRows,
    ]);

    const closeLevelDialog = React.useCallback(() => {
        if (levelSaving) return;
        setLevelDialogOpen(false);
    }, [levelSaving]);

    const setLevelRowField =
        (index: number, key: keyof AssessmentLevelDetail) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const nextValue = Number(event.target.value);
            setLevelRows(prev =>
                prev.map((row, rowIndex) =>
                    rowIndex === index
                        ? {
                              ...row,
                              [key]: Number.isFinite(nextValue) ? nextValue : 0,
                          }
                        : row
                )
            );
        };

    const addLevelRow = React.useCallback(() => {
        setLevelRows(prev => {
            const sorted = [...prev].sort((a, b) => a.level - b.level);
            const last = sorted[sorted.length - 1];
            if (!last) {
                return [{ ...defaultLevelDetail }];
            }

            const nextLevel = Number(last.level) + 1;
            const nextAgeMin = Number(last.ageMax) + 1;

            return [
                ...prev,
                {
                    level: nextLevel,
                    ageMin: nextAgeMin,
                    ageMax: nextAgeMin,
                },
            ];
        });
    }, []);

    const removeLevelRow = React.useCallback((index: number) => {
        setLevelRows(prev => {
            const target = prev[index];
            if (target?.id && usedLevelIds.has(target.id)) {
                notify(`Level ${target.level} is in use and cannot be deleted`, {
                    type: 'warning',
                });
                return prev;
            }

            if (target?.id) {
                setDeletedLevelIds(ids =>
                    ids.includes(target.id as number)
                        ? ids
                        : [...ids, target.id as number]
                );
            }

            return prev.filter((_, rowIndex) => rowIndex !== index);
        });
    }, [notify, usedLevelIds]);

    const validateLevelRows = React.useCallback((rows: AssessmentLevelDetail[]) => {
        if (!rows.length) {
            return 'Level data is required';
        }

        const sorted = [...rows].sort((a, b) => a.level - b.level);

        for (let index = 0; index < sorted.length; index += 1) {
            const row = sorted[index];
            if (!Number.isFinite(row.level) || !Number.isFinite(row.ageMin) || !Number.isFinite(row.ageMax)) {
                return 'Level, ageMin and ageMax must be numbers';
            }

            if (row.ageMin > row.ageMax) {
                return `Level ${row.level}: ageMin cannot be greater than ageMax`;
            }

            if (index > 0) {
                const previous = sorted[index - 1];
                if (row.ageMin !== previous.ageMax + 1) {
                    return `Level ${row.level}: age range cannot overlap or break in the middle`;
                }
            }
        }

        return '';
    }, []);

    const saveAssessmentLevels = React.useCallback(async () => {
        const normalized = normalizeLevelRows(levelRows);
        if (normalized.length > 0) {
            const validationError = validateLevelRows(normalized);
            if (validationError) {
                setLevelError(validationError);
                notify(validationError, { type: 'warning' });
                return;
            }
        }

        const deletePayload = deletedLevelIds.map(id => ({
            id,
            deleted: true,
        }));

        if (!normalized.length && !deletePayload.length) {
            const message = 'No level changes to save';
            setLevelError(message);
            notify(message, { type: 'warning' });
            return;
        }

        setLevelSaving(true);
        setLevelError('');
        try {
            const updatePayload = normalized.map(item => {
                if (item.id) {
                    return {
                        id: item.id,
                        level: item.level,
                        ageMin: item.ageMin,
                        ageMax: item.ageMax,
                    };
                }

                return {
                    level: item.level,
                    ageMin: item.ageMin,
                    ageMax: item.ageMax,
                };
            });

            const payload = [...updatePayload, ...deletePayload];

            await dataProvider.update('admin/assessment-levels', {
                id: 'batch',
                data: payload,
                previousData: {},
            });

            notify('Assessment levels updated', { type: 'success' });
            setLevelDialogOpen(false);
            setDeletedLevelIds([]);
            refresh();
        } catch (error: any) {
            const message = error?.message || 'Failed to update levels';
            setLevelError(message);
            notify(message, { type: 'error' });
        } finally {
            setLevelSaving(false);
        }
    }, [
        dataProvider,
        deletedLevelIds,
        levelRows,
        normalizeLevelRows,
        notify,
        refresh,
        validateLevelRows,
    ]);

    const openEditDialog = React.useCallback((record: AssessmentRecord) => {
        setDialogMode('edit');
        setEditingId(record.id);
        setUploadError('');
        setUploadStatus('');
        setUploadProgress(0);
        setStreamState('');
        setCheckingStreamReady(false);
        setPreviewPlaybackUrl(getStreamPlayerUrl(record.uid || ''));
        setFormState({
            dimensionId: getAssessmentDimensionId(record) ?? 0,
            levelId: getAssessmentLevelId(record) ?? 0,
            title: record.title || '',
            description: record.description || '',
            content: record.content || '',
            uid: record.uid || '',
            status: Number(record.status ?? 9),
        });
        setDialogOpen(true);
    }, []);

    const closeDialog = React.useCallback(() => {
        if (saving || uploadingVideo) return;
        setDialogOpen(false);
    }, [saving, uploadingVideo]);

    const openVideoFileDialog = React.useCallback(() => {
        videoInputRef.current?.click();
    }, []);

    const pollStreamReady = React.useCallback(async (uid: string) => {
        setCheckingStreamReady(true);
        try {
            for (let attempt = 0; attempt < 24; attempt += 1) {
                const token = await getAccessToken();
                const headers: HeadersInit = {};
                if (token) {
                    headers.Authorization = `Bearer ${token}`;
                }

                const response = await fetch(`${API_BASE_URL}/api/upload/stream/${uid}`, {
                    method: 'GET',
                    headers,
                });

                const payload = await response.json().catch(() => null);
                if (response.ok) {
                    if (typeof payload?.code === 'number' && payload.code !== 200) {
                        await new Promise(resolve => setTimeout(resolve, 5000));
                        continue;
                    }

                    const current = payload?.data || payload?.result || payload;
                    const nextState = normalizeStreamState(current?.status?.state || current?.state);

                    if (nextState) {
                        setStreamState(nextState);
                    }

                    if (!nextState || isReadyStreamState(nextState)) {
                        setStreamState('ready');
                        setPreviewPlaybackUrl(current?.playbackUrl || getStreamPlayerUrl(uid));
                        setUploadStatus('');
                        setUploadError('');
                        return;
                    }

                    setUploadStatus(`Video is ${nextState}, waiting to be ready...`);
                }

                await new Promise(resolve => setTimeout(resolve, 5000));
            }
        } catch {
            // Keep current state and let user retry if needed.
        } finally {
            setCheckingStreamReady(false);
        }
    }, []);

    const handleVideoFileChange = React.useCallback(
        async (event: React.ChangeEvent<HTMLInputElement>) => {
            const file = event.target.files?.[0];
            if (!file) return;

            setUploadingVideo(true);
            setUploadError('');
            setUploadStatus('');
            setUploadProgress(0);
            setStreamState('');
            try {
                const formData = new FormData();
                formData.append('file', file, file.name);

                const token = await getAccessToken();
                const headers: HeadersInit = {};
                if (token) {
                    headers.Authorization = `Bearer ${token}`;
                }

                const result = await new Promise<any>((resolve, reject) => {
                    const xhr = new XMLHttpRequest();
                    xhr.open('POST', `${API_BASE_URL}/api/upload/stream`, true);
                    if (headers.Authorization) {
                        xhr.setRequestHeader('Authorization', headers.Authorization as string);
                    }

                    xhr.upload.onprogress = progressEvent => {
                        if (!progressEvent.lengthComputable) return;
                        const percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                        setUploadProgress(percent);
                    };

                    xhr.onerror = () => reject(new Error('Network error, upload failed'));
                    xhr.onabort = () => reject(new Error('Upload aborted'));
                    xhr.onload = () => {
                        let parsed: any = null;
                        try {
                            parsed = xhr.responseText ? JSON.parse(xhr.responseText) : null;
                        } catch {
                            parsed = null;
                        }

                        if (xhr.status < 200 || xhr.status >= 300) {
                            reject(new Error(parsed?.error || parsed?.message || 'Video upload failed'));
                            return;
                        }

                        resolve(parsed || {});
                    };

                    xhr.send(formData);
                });

                if (typeof result?.code === 'number' && result.code !== 200) {
                    throw new Error(result?.message || 'Video upload failed');
                }

                const nextUid = (
                    result?.data?.uid ||
                    result?.uid ||
                    ''
                ).toString();
                if (!nextUid) {
                    throw new Error('Upload succeeded but uid is missing');
                }

                setFormState(prev => ({
                    ...prev,
                    uid: nextUid,
                }));
                const nextState = normalizeStreamState(result?.data?.state || result?.state);
                setStreamState(nextState);
                setPreviewPlaybackUrl(result?.data?.playbackUrl || result?.playbackUrl || getStreamPlayerUrl(nextUid));
                setUploadProgress(100);
                notify('Video uploaded successfully', { type: 'success' });

                if (nextState && !isReadyStreamState(nextState)) {
                    setUploadStatus(`Video is ${nextState}, waiting to be ready...`);
                    void pollStreamReady(nextUid);
                } else {
                    setUploadStatus('');
                    setUploadError('');
                }
            } catch (error: any) {
                setUploadStatus('');
                setUploadError(error?.message || 'Failed to upload video');
                notify(error?.message || 'Failed to upload video', { type: 'error' });
            } finally {
                setUploadingVideo(false);
                event.target.value = '';
            }
        },
        [notify, pollStreamReady]
    );

    const currentStreamState = normalizeStreamState(streamState);
    const isPreviewPlayable = Boolean(formState.uid.trim()) && (!currentStreamState || isReadyStreamState(currentStreamState));

    const setField =
        (key: keyof AssessmentFormState) =>
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const nextValue =
                key === 'dimensionId' || key === 'levelId' || key === 'status'
                    ? Number(event.target.value)
                    : event.target.value;

            setFormState(prev => ({
                ...prev,
                [key]: nextValue,
            }));
        };

    const handleSubmit = async () => {
        if (!formState.title.trim()) {
            notify('Title is required', { type: 'warning' });
            return;
        }

        if (!formState.dimensionId || !formState.levelId) {
            notify('Dimension ID and Level ID are required', { type: 'warning' });
            return;
        }

        const hasDuplicateTarget = records.some(record => {
            const targetDimensionId = getAssessmentDimensionId(record);
            const targetLevelId = getAssessmentLevelId(record);

            if (targetDimensionId === undefined || targetLevelId === undefined) {
                return false;
            }

            if (
                targetDimensionId !== Number(formState.dimensionId) ||
                targetLevelId !== Number(formState.levelId)
            ) {
                return false;
            }

            if (dialogMode === 'edit' && editingId !== null && record.id === editingId) {
                return false;
            }

            return true;
        });

        if (hasDuplicateTarget) {
            notify('Target Level and Dimension already exists, cannot create or update', {
                type: 'warning',
            });
            return;
        }

        setSaving(true);
        try {
            const payload = {
                title: formState.title.trim(),
                uid: formState.uid.trim(),
                dimensionId: Number(formState.dimensionId),
                levelId: Number(formState.levelId),
                description: formState.description,
                content: formState.content,
                status: Number(formState.status),
            };

            if (dialogMode === 'edit' && editingId !== null) {
                await dataProvider.update('admin/assessments', {
                    id: editingId,
                    data: payload,
                    previousData: {
                        id: editingId,
                    },
                });
                notify('Assessment updated', { type: 'success' });
            } else {
                await dataProvider.create('admin/assessments', {
                    data: payload,
                });
                notify('Assessment created', { type: 'success' });
            }

            setDialogOpen(false);
            refresh();
        } catch (error: any) {
            notify(error?.message || 'Save failed', { type: 'error' });
        } finally {
            setSaving(false);
        }
    };

    const handleDeleteAssessment = React.useCallback(async () => {
        if (dialogMode !== 'edit' || editingId === null) return;

        const confirmed = window.confirm(
            'Deleting this assessment will also remove related player evaluation data. Do you want to continue?'
        );

        if (!confirmed) return;

        setDeletingAssessment(true);
        try {
            await dataProvider.delete('admin/assessments', {
                id: editingId,
                previousData: { id: editingId },
            });

            notify('Assessment deleted', { type: 'success' });
            setDialogOpen(false);
            refresh();
        } catch (error: any) {
            notify(error?.message || 'Delete failed', { type: 'error' });
        } finally {
            setDeletingAssessment(false);
        }
    }, [dataProvider, dialogMode, editingId, notify, refresh]);

    if (isPending) {
        return (
            <Box p={2}>
                <Typography variant="body2" color="text.secondary">
                    Loading...
                </Typography>
            </Box>
        );
    }

    if (!records.length && (!dimensionOptions.length || !levelOptions.length)) {
        return (
            <Box p={2}>
                <Typography variant="body2" color="text.secondary">
                    No data
                </Typography>
            </Box>
        );
    }

    return (
        <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ overflowX: 'auto' }}
        >
            <Table
                size="small"
                sx={{
                    tableLayout: 'fixed',
                    minWidth: `${tableMinWidth}px`,
                    borderCollapse: 'collapse',
                    '& .MuiTableCell-root': {
                        border: '1px solid',
                        borderColor: 'divider',
                    },
                    '& .MuiTableHead-root .MuiTableCell-root': {
                        border: '1px solid',
                        borderColor: 'divider',
                    },
                    '& .MuiTableBody-root .MuiTableCell-root': {
                        border: '1px solid',
                        borderColor: 'divider',
                    },
                    '& .MuiTableRow-root:last-child .MuiTableCell-root': {
                        borderBottom: '1px solid',
                        borderColor: 'divider',
                    },
                }}
            >
                <TableHead>
                    <TableRow>
                        <TableCell
                            onClick={openLevelDialog}
                            sx={{
                                minWidth: LEVEL_COLUMN_WIDTH,
                                width: LEVEL_COLUMN_WIDTH,
                                maxWidth: LEVEL_COLUMN_WIDTH,
                                fontWeight: 700,
                                backgroundColor: 'action.hover',
                                border: '1px solid',
                                borderColor: 'divider',
                                cursor: 'pointer',
                            }}
                        >
                            Level
                        </TableCell>
                        {dimensionOptions.map(dimension => (
                            <TableCell
                                key={dimension.id}
                                onClick={openDimensionDialog}
                                sx={{
                                    width: MATRIX_CELL_SIZE,
                                    minWidth: MATRIX_CELL_SIZE,
                                    maxWidth: MATRIX_CELL_SIZE,
                                    fontWeight: 700,
                                    backgroundColor: 'action.hover',
                                    border: '1px solid',
                                    borderColor: 'divider',
                                    cursor: 'pointer',
                                }}
                            >
                                {dimension.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {levelOptions.map(level => (
                        <TableRow key={level.id} hover>
                            <TableCell
                                sx={{
                                    minWidth: LEVEL_COLUMN_WIDTH,
                                    width: LEVEL_COLUMN_WIDTH,
                                    maxWidth: LEVEL_COLUMN_WIDTH,
                                    fontWeight: 700,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                {level.label}
                            </TableCell>
                            {dimensionOptions.map(dimension => {
                                const levelMap = grouped.get(level.id);
                                const cellRecords = levelMap?.get(dimension.id) || [];
                                return (
                                    <TableCell
                                        key={`${level.id}-${dimension.id}`}
                                        onClick={() => openCreateDialog(level.id, dimension.id)}
                                        sx={{
                                            position: 'relative',
                                            width: MATRIX_CELL_SIZE,
                                            minWidth: MATRIX_CELL_SIZE,
                                            maxWidth: MATRIX_CELL_SIZE,
                                            padding: 1,
                                            cursor: 'pointer',
                                            verticalAlign: cellRecords.length ? 'top' : 'middle',
                                            border: '1px solid',
                                            borderColor: 'divider',
                                            '&:hover': {
                                                backgroundColor: 'action.hover',
                                            },
                                            '& .cell-edit-button': {
                                                opacity: 0,
                                                pointerEvents: 'none',
                                                transition: 'opacity 0.2s ease',
                                            },
                                            '&:hover .cell-edit-button': {
                                                opacity: 1,
                                                pointerEvents: 'auto',
                                            },
                                        }}
                                    >
                                        {cellRecords.length > 0 ? (
                                            <Tooltip title="Edit" arrow>
                                                <IconButton
                                                    size="small"
                                                    className="cell-edit-button"
                                                    onClick={event => {
                                                        event.stopPropagation();
                                                        openEditDialog(cellRecords[0]);
                                                    }}
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 4,
                                                        right: 4,
                                                        zIndex: 2,
                                                        backgroundColor: 'background.paper',
                                                        border: '1px solid',
                                                        borderColor: 'divider',
                                                        '&:hover': {
                                                            backgroundColor: 'action.hover',
                                                        },
                                                    }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        ) : null}
                                        <CellContent
                                            records={cellRecords}
                                            onEditRecord={openEditDialog}
                                        />
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
                <DialogTitle>
                    {dialogMode === 'edit' ? 'Edit Assessment' : 'Create Assessment'}
                </DialogTitle>
                <DialogContent>
                    <input
                        ref={videoInputRef}
                        type="file"
                        accept="video/*"
                        style={{ display: 'none' }}
                        onChange={handleVideoFileChange}
                    />
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        <TextField
                            select
                            label="Dimension"
                            value={formState.dimensionId}
                            onChange={setField('dimensionId')}
                            fullWidth
                        >
                            {dimensionOptions.map(option => (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            label="Level"
                            value={formState.levelId}
                            onChange={setField('levelId')}
                            fullWidth
                        >
                            {levelOptions.map(option => (
                                <MenuItem key={option.id} value={option.id}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            label="Title"
                            value={formState.title}
                            onChange={setField('title')}
                            fullWidth
                            required
                        />
                        <TextField
                            label="Description"
                            value={formState.description}
                            onChange={setField('description')}
                            fullWidth
                            multiline
                            minRows={2}
                        />
                        <TextField
                            label="Content"
                            value={formState.content}
                            onChange={setField('content')}
                            fullWidth
                            multiline
                            minRows={2}
                        />
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Button
                                variant="outlined"
                                onClick={openVideoFileDialog}
                                disabled={saving || uploadingVideo}
                                startIcon={uploadingVideo ? <CircularProgress size={16} /> : <UploadFileIcon />}
                            >
                                {formState.uid ? 'Reupload Video' : 'Upload Video'}
                            </Button>
                            <Box sx={{ flexGrow: 1, minWidth: 180, display: 'flex', alignItems: 'center', gap: 1 }}>
                                {uploadingVideo ? (
                                    <>
                                        <LinearProgress
                                            variant="determinate"
                                            value={uploadProgress}
                                            sx={{ flexGrow: 1, height: 8, borderRadius: 999 }}
                                        />
                                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 40 }}>
                                            {uploadProgress}%
                                        </Typography>
                                    </>
                                ) : null}
                            </Box>
                        </Stack>
                        {uploadError ? (
                            <Typography variant="body2" color="error.main">
                                {uploadError}
                            </Typography>
                        ) : null}
                        {uploadStatus ? (
                            <Typography variant="body2" color="text.secondary">
                                {uploadStatus}
                            </Typography>
                        ) : null}
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                            Video Preview
                        </Typography>
                        <Box
                            sx={{
                                width: '100%',
                                aspectRatio: '16 / 9',
                                borderRadius: 1,
                                overflow: 'hidden',
                                border: '1px solid',
                                borderColor: 'divider',
                                backgroundColor: '#000',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            {isPreviewPlayable ? (
                                <Box
                                    component="iframe"
                                    src={previewPlaybackUrl || getStreamPlayerUrl(formState.uid.trim())}
                                    loading="lazy"
                                    allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
                                    allowFullScreen
                                    sx={{
                                        width: '100%',
                                        height: '100%',
                                        border: 0,
                                        display: 'block',
                                        backgroundColor: '#000',
                                    }}
                                />
                            ) : formState.uid.trim() ? (
                                <Stack spacing={1} alignItems="center" sx={{ px: 2 }}>
                                    {(uploadingVideo || checkingStreamReady) ? <CircularProgress size={20} /> : null}
                                    <Typography variant="body2" color="grey.400" textAlign="center">
                                        Video is processing ({currentStreamState || 'queued'}), please wait...
                                    </Typography>
                                </Stack>
                            ) : (
                                <Typography variant="body2" color="grey.400">
                                    Upload video to preview
                                </Typography>
                            )}
                        </Box>
                        <TextField
                            select
                            label="Status"
                            value={formState.status}
                            onChange={setField('status')}
                            fullWidth
                        >
                            {statusChoices.map(choice => (
                                <MenuItem key={choice.id} value={choice.id}>
                                    {choice.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    {dialogMode === 'edit' ? (
                        <Button
                            color="error"
                            onClick={handleDeleteAssessment}
                            disabled={saving || uploadingVideo || deletingAssessment}
                        >
                            Delete
                        </Button>
                    ) : null}
                    <Button onClick={closeDialog} disabled={saving || uploadingVideo || deletingAssessment}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleSubmit}
                        variant="contained"
                        disabled={saving || uploadingVideo || deletingAssessment}
                    >
                        {dialogMode === 'edit' ? 'Update' : 'Create'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={levelDialogOpen} onClose={closeLevelDialog} fullWidth maxWidth="md">
                <DialogTitle>Assessment Levels</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {levelLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <>
                                <Stack direction="row" justifyContent="flex-end">
                                    <Button onClick={addLevelRow} startIcon={<AddIcon />} variant="outlined">
                                        Add Level
                                    </Button>
                                </Stack>
                                {levelRows.map((row, index) => (
                                    <Stack key={row.id || `new-${index}`} direction="row" spacing={2}>
                                        <TextField
                                            label="ID"
                                            value={row.id ?? 'New'}
                                            fullWidth
                                            disabled
                                        />
                                        <TextField
                                            label="Level"
                                            type="number"
                                            value={row.level}
                                            onChange={setLevelRowField(index, 'level')}
                                            fullWidth
                                        />
                                        <TextField
                                            label="Age Min"
                                            type="number"
                                            value={row.ageMin}
                                            onChange={setLevelRowField(index, 'ageMin')}
                                            fullWidth
                                        />
                                        <TextField
                                            label="Age Max"
                                            type="number"
                                            value={row.ageMax}
                                            onChange={setLevelRowField(index, 'ageMax')}
                                            fullWidth
                                        />
                                        <IconButton
                                            color="error"
                                            onClick={() => removeLevelRow(index)}
                                            sx={{ alignSelf: 'center' }}
                                            disabled={Boolean(row.id && usedLevelIds.has(row.id))}
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </Stack>
                                ))}
                            </>
                        )}
                        {levelError ? (
                            <Typography variant="body2" color="error.main">
                                {levelError}
                            </Typography>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeLevelDialog} disabled={levelSaving}>
                        Cancel
                    </Button>
                    <Button
                        onClick={saveAssessmentLevels}
                        variant="contained"
                        disabled={levelLoading || levelSaving}
                    >
                        {levelSaving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog open={dimensionDialogOpen} onClose={closeDimensionDialog} fullWidth maxWidth="md">
                <DialogTitle>Assessment Dimensions</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {dimensionLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                <CircularProgress size={24} />
                            </Box>
                        ) : (
                            <>
                                <Stack direction="row" justifyContent="flex-end">
                                    <Button onClick={addDimensionRow} startIcon={<AddIcon />} variant="outlined">
                                        Add Dimension
                                    </Button>
                                </Stack>
                                {dimensionRows.map((row, index) => (
                                    <Stack key={row.id || `new-dimension-${index}`} direction="row" spacing={2}>
                                        <TextField
                                            label="ID"
                                            value={row.id ?? 'New'}
                                            fullWidth
                                            disabled
                                        />
                                        <TextField
                                            label="Dimension"
                                            value={row.dimension}
                                            onChange={setDimensionRowField(index, 'dimension')}
                                            fullWidth
                                        />
                                        <TextField
                                            label="Idx"
                                            type="number"
                                            value={row.idx}
                                            onChange={setDimensionRowField(index, 'idx')}
                                            fullWidth
                                        />
                                        <IconButton
                                            color="error"
                                            onClick={() => removeDimensionRow(index)}
                                            sx={{ alignSelf: 'center' }}
                                            disabled={Boolean(
                                                row.id && usedDimensionIds.has(row.id)
                                            )}
                                        >
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </Stack>
                                ))}
                            </>
                        )}
                        {dimensionError ? (
                            <Typography variant="body2" color="error.main">
                                {dimensionError}
                            </Typography>
                        ) : null}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeDimensionDialog} disabled={dimensionSaving}>
                        Cancel
                    </Button>
                    <Button
                        onClick={saveAssessmentDimensions}
                        variant="contained"
                        disabled={dimensionLoading || dimensionSaving}
                    >
                        {dimensionSaving ? 'Saving...' : 'Save'}
                    </Button>
                </DialogActions>
            </Dialog>
        </TableContainer>
    );
};

const AssessmentList = () => (
    <List
        title="Assessment"
        perPage={100}
        pagination={false}
        actions={false}
        exporter={false}
        sort={{ field: 'id', order: 'ASC' }}
    >
        <AssessmentMatrixTable />
    </List>
);

export default AssessmentList;
